import { useRef, useState } from 'react';
import { Editor } from '@tiptap/react';
import { Image as ImageIcon, Video, Smile } from 'lucide-react';
import { Button } from '../../core/button';
import { useToast } from '../../core/toast';
import type { MediaUploadResult } from './types';
import { EmojiPickerSheet } from './emoji-picker-sheet';

type Props = {
  editor: Editor;
  uploadMedia: (file: File) => Promise<MediaUploadResult>;
  hint?: string;
};

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_VIDEO_BYTES = 5 * 1024 * 1024 * 1024;

export function RichTextActionBar({ editor, uploadMedia, hint }: Props) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file: File, expected: 'image' | 'video') => {
    const types = expected === 'image' ? IMAGE_TYPES : VIDEO_TYPES;
    const max = expected === 'image' ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES;
    if (!types.includes(file.type)) {
      toast({
        title: 'Unsupported file type',
        description: `${expected === 'image' ? 'Image' : 'Video'} must be one of: ${types.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }
    if (file.size > max) {
      toast({
        title: 'File too large',
        description: `Maximum ${expected} size is ${expected === 'image' ? '20 MB' : '5 GB'}.`,
        variant: 'destructive',
      });
      return;
    }
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      if (result.kind === 'image') {
        editor.chain().focus().insertContent({ type: 'image', attrs: { src: result.url, alt: '' } }).run();
      } else {
        editor.chain().focus().insertContent({ type: 'video', attrs: { src: result.url } }).run();
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-1 border-t border-border p-2">
      <Button type="button" variant="ghost" size="sm" disabled={uploading} aria-label="Insert image" onClick={() => imageInputRef.current?.click()}>
        <ImageIcon className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" disabled={uploading} aria-label="Insert video" onClick={() => videoInputRef.current?.click()}>
        <Video className="h-4 w-4" />
      </Button>
      <div className="relative">
        <Button type="button" variant="ghost" size="sm" aria-label="Insert emoji" onClick={() => setPickerOpen((v) => !v)}>
          <Smile className="h-4 w-4" />
        </Button>
        {pickerOpen && (
          <EmojiPickerSheet
            onSelect={(e) => {
              editor.chain().focus().insertContent({ type: 'emoji', attrs: { unicode: e.native, shortcode: `:${e.id}:` } }).run();
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
      <input ref={imageInputRef} type="file" accept={IMAGE_TYPES.join(',')} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, 'image'); e.target.value = ''; }} />
      <input ref={videoInputRef} type="file" accept={VIDEO_TYPES.join(',')} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, 'video'); e.target.value = ''; }} />
      {hint && <span className="ml-auto pl-2 text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}
